/**
 * PROJECT ARCHITECTURE & COMPLIANCE SUMMARY (INTERNAL DOCS)
 *
 * 1. TECHNOLOGY STACK:
 *    - Framework: Vite + React + TSX.
 *    - Styling: Strict Tailwind CSS (Injected via TSX).
 *
 * 2. COMPLIANCE CONSTRAINTS:
 *    - [CRITICAL] No .css files allowed in the source tree.
 *    - [CRITICAL] No .md files allowed in the root (README/Walkthroughs).
 *
 * 3. STYLING SYSTEM:
 *    - Global styles are handled via 'index.css' (Tailwind directives).
 *    - All component styling is handled via utility classes.
 *    - Avoid inline 'style' props; use Tailwind utility classes exclusively.
 */

import './index.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ThemeProvider } from './context/ThemeContext';
import { DataProvider } from './context/DataContext';
import { GlobalStyles } from './theme/GlobalStyles';
import ErrorBoundary from './components/common/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <GlobalStyles />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ErrorBoundary>
          <ThemeProvider>
            <DataProvider>
              <App />
            </DataProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
